/**
 * Glue (rules v2) view helpers, free of Phaser so they are unit-tested in node: which pairs
 * show a ribbon, which tiles an undo or rescue returns, and which collected tile flies a
 * beat later. Core decides every rule; these only read its state and events.
 */
import { PIECE_BOARD, isTappable, type CompiledLevel, type Round, type RoundEvent } from '@mp/core';

/** Glued pairs of a level, each once as [lower tile, higher tile]. */
export function gluePairs(L: CompiledLevel): [number, number][] {
  const out: [number, number][] = [];
  if (!L.nGlue) return out;
  for (let i = 0; i < L.nBoard; i++) {
    const p = L.partner[i]!;
    if (p > i) out.push([i, p]);
  }
  return out;
}

/**
 * A pair shows its ribbon while both tiles are on the board (a tile whose partner left plays
 * as a single, no ribbon). It is dimmed while either tile is covered.
 */
export function ribbonState(r: Round, a: number, b: number): { on: boolean; covered: boolean } {
  const on = r.piece[a] === PIECE_BOARD && r.piece[b] === PIECE_BOARD;
  return { on, covered: on && (r.cover[a]! > 0 || r.cover[b]! > 0) };
}

/** Tiles an event sends from the bag back to the board: an undone pair returns both. */
export function returnedTiles(e: RoundEvent): number[] {
  if (e.type === 'undone') return e.partner === undefined ? [e.tile] : [e.tile, e.partner];
  if (e.type === 'rescued') return e.returned;
  return [];
}

/**
 * Per-tile flight delay for one event batch: a glued partner leaves the board a beat after
 * the tapped tile, so the two read as one tap pulling the other along.
 */
export function flightDelays(events: readonly RoundEvent[], partnerDelayMs: number): Map<number, number> {
  const out = new Map<number, number>();
  for (const e of events) if (e.type === 'glued') out.set(e.partner, partnerDelayMs);
  return out;
}

/** Every piece a tap would take right now: both tiles of a free pair (tappableTiles lists a pair once). */
export function tappablePieces(r: Round): number[] {
  const out: number[] = [];
  for (let i = 0; i < r.L.n; i++) if (isTappable(r, i)) out.push(i);
  return out;
}

/**
 * Bag slot spacing: `count` items over `cap` slots of `slot` px. A glued pair into the last
 * free slot briefly holds cap + 1 items; they squeeze into the bag's width instead of spilling.
 */
export function slotStep(cap: number, count: number, slot: number): number {
  const n = Math.max(cap, count);
  return n > cap ? (slot * cap - slot) / (n - 1) : slot;
}
